import {
  Home as HomeIcon,
  HeartHandshake,
  Sprout,
  Flower2,
  BookOpen,
  Soup,
  GraduationCap,
  HeartPulse,
  Leaf,
  Landmark,
  HandHeart,
} from "lucide-react";

export const IMAGES = {
  banner: "/assets/banner.png",
  praying: "/assets/hero.jpg",
  ceremony: "/assets/monks2.png",
  outdoor: "/assets/monks1.png",
  portrait: "/assets/bodhidatta.png",
  svcDhammaMeditation: "/assets/services/dhamma-meditation.png",
  svcNalanda: "/assets/services/environment.png",
  svcAnnaDana: "/assets/services/anna-dana.png",
  svcEducation: "/assets/services/education.png",
  svcMedicalCamps: "/assets/services/medical-camps.png",
  svcBuddhistHeritage: "/assets/services/buddhist-heritage.png",
  svcKarunaSeva: "/assets/services/karuna.png",
  svcDhamma: "/assets/services/dhamma.png",
};

export const GALLERY = [
  { src: "/assets/gallery/g4.webp", alt: "IBMCT monastics seated in chanting and prayer" },
  { src: "/assets/gallery/g2.webp", alt: "A large gathering of monks in seated prayer at an IBMCT event" },
  { src: "/assets/gallery/g6.webp", alt: "Lamp-lighting ceremony with monastics and dignitaries at Maha Karuna" },
  { src: "/assets/gallery/g1.webp", alt: "Monks standing in procession with offerings" },
  { src: "/assets/gallery/g3.webp", alt: "Dignitaries lighting the ceremonial lamp at an IBMCT event" },
  { src: "/assets/gallery/g5.webp", alt: "Monastics assembled with offerings during a ground blessing" },
];

export const programs = [
  {
    id: "namma-mane",
    icon: HomeIcon,
    name: "Namma Mane",
    knName: "ನಮ್ಮ ಮನೆ",
    tagline: "A Home With Dignity",
    desc: "A residential care home for underprivileged elders with no family support — providing shelter, nutritious meals, medical care, companionship and a dignified environment.",
    image: "praying",
    emblem: "/assets/programs/namma-mane.png",
    emblemBg: "#F3EBDD",
    scene: "/assets/programs/scene-namma-mane.png",
    facts: [
      ["Full Vision", "₹30 Crores — 1,500 to 2,000 elders"],
      ["Phase 1", "Around 500 elders"],
      ["Provided Free", "Accommodation, food & healthcare"],
    ],
    facilities: [
      "Beautiful gardens",
      "Walking paths",
      "Meditation spaces",
      "Prayer & spiritual areas",
      "Recreational facilities",
      "Dining facilities",
      "Healthcare services",
    ],
    long: [
      "“Namma Mane” is not merely a shelter for destitute senior citizens; it is a warm and compassionate home that gives elderly lives who may have lost love, family, and hope, a renewed sense of dignity and belonging.",
      "Every person deserves to spend the later years of life with care, respect, security, and companionship.",
      "With an estimated project cost of ₹30 crore, this initiative is being planned to provide a safe and comfortable home for approximately 1,500 to 2,000 senior citizens, along with the necessary facilities and services for their well-being.",
      "As part of the “Namma Mane” project, IBMCT will initially provide free accommodation, nutritious food, healthcare, and other essential facilities for around 500 elderly people.",
      "The campus will include beautiful gardens, walking paths, meditation spaces, prayer and spiritual areas, recreational facilities, dining facilities, healthcare services, and other amenities required for the physical, mental, and emotional well-being of senior citizens.",
      "It is said that every elderly person deserves a dignified and peaceful life. “You are not alone; this is your home” — this is the assurance that “Namma Mane” seeks to provide.",
      "“Namma Mane” is not just a shelter; it is a place where elderly people can live with dignity, peace, love, care, and a sense of belonging.",
      "A compassionate initiative by IBMCT – International Buddhist Monks Charitable Trust.",
    ],
    kn: {
      tagline: "ಗೌರವದ ಮನೆ",
      desc: "ಆಸರೆಯಿಲ್ಲದ ಹಿರಿಯರಿಗೆ ಪ್ರೀತಿ, ಗೌರವ ಮತ್ತು ನೆಮ್ಮದಿಯ ಸುರಕ್ಷಿತ ಮನೆ — ಆಶ್ರಯ, ಪೌಷ್ಟಿಕ ಊಟ, ವೈದ್ಯಕೀಯ ಆರೈಕೆ ಮತ್ತು ಆತ್ಮೀಯತೆ.",
      facts: [
        ["ಪೂರ್ಣ ಸಂಕಲ್ಪ", "₹30 ಕೋಟಿ — 1,500–2,000 ಹಿರಿಯರು"],
        ["ಮೊದಲ ಹಂತ", "ಸುಮಾರು 500 ಹಿರಿಯರಿಗೆ"],
        ["ಉಚಿತವಾಗಿ", "ವಸತಿ, ಆಹಾರ ಮತ್ತು ಆರೋಗ್ಯ ಸೇವೆ"],
      ],
      facilities: [
        "ಆರೋಗ್ಯ ಚಿಕಿತ್ಸಾ ಕೇಂದ್ರ",
        "ಸುಸಜ್ಜಿತ ವಸತಿ ಕೊಠಡಿಗಳು",
        "ಯೋಗ ಮತ್ತು ಧ್ಯಾನ ಕೊಠಡಿ",
        "ಲಘು ವ್ಯಾಯಾಮ ಕೊಠಡಿ",
        "ಒಳಾಂಗಣ ಕ್ರೀಡಾ ಕೊಠಡಿ",
        "ಮನೋರಂಜನಾ ಕೊಠಡಿ",
        "ಭೋಜನಾಲಯ",
        "ಹಸಿರುಮಯ ಹೊರಾಂಗಣ ಉದ್ಯಾನವನ",
      ],
      long: [
        "“ನಮ್ಮ ಮನೆ” ನಿರಾಶ್ರಿತ ಹಾಗೂ ಆಸರೆಯಿಲ್ಲದ ವಯೋವೃದ್ಧರಿಗಾಗಿ ನಿರ್ಮಿಸಲಾಗುತ್ತಿರುವ ಒಂದು ಪ್ರೀತಿಯ ಆಶ್ರಯಧಾಮ. ಯಾವುದೇ ಹಿರಿಯ ಜೀವಿಗೂ ತಾನು ಒಂಟಿ ಅಥವಾ ಅನಾಥ ಎಂಬ ಭಾವನೆ ಕಾಡದಂತೆ, ಜೀವನದ ಸಂಧ್ಯಾಕಾಲದಲ್ಲಿ ನೆಮ್ಮದಿ, ಗೌರವ, ಆತ್ಮೀಯತೆ ಮತ್ತು ಸ್ವಾತಂತ್ರ್ಯದೊಂದಿಗೆ ಬದುಕಲು ಅವಕಾಶ ಕಲ್ಪಿಸುವ ಉಚಿತ ಆಶ್ರಯ ತಾಣ ಇದು.",
        "ಸುಮಾರು 3 ಎಕರೆ ವಿಸ್ತೀರ್ಣದಲ್ಲಿ ಹಂತ ಹಂತವಾಗಿ ಅಭಿವೃದ್ಧಿಗೊಳ್ಳಲಿರುವ ಈ ಯೋಜನೆಯು ಒಟ್ಟು ಅಂದಾಜು ₹30 ಕೋಟಿ ವೆಚ್ಚದಲ್ಲಿ 1,500 ರಿಂದ 2,000 ವಯೋವೃದ್ಧರಿಗೆ ಆಶ್ರಯ ನೀಡುವ ಸಂಕಲ್ಪ ಹೊಂದಿದೆ. ಮೊದಲ ಹಂತವಾಗಿ ಸುಮಾರು ₹18 ಕೋಟಿ ವೆಚ್ಚದಲ್ಲಿ 500 ವಯೋವೃದ್ಧರಿಗೆ ಅನುಕೂಲವಾಗುವ ಸುಸಜ್ಜಿತ ಕಟ್ಟಡಗಳು ಮತ್ತು ವಸತಿ ಕೊಠಡಿಗಳನ್ನು ನಿರ್ಮಿಸಲಾಗುತ್ತದೆ.",
        "ಇಲ್ಲಿ ಬರುವ ಪ್ರತಿಯೊಬ್ಬ ಹಿರಿಯ ಜೀವಿಗೂ ಕೇವಲ ಒಂದು ಕೊಠಡಿ ಅಥವಾ ಒಂದು ಊಟವಲ್ಲ, “ನೀವು ಒಂಟಿಯಲ್ಲ — ಇದು ನಿಮ್ಮ ಮನೆ” ಎಂಬ ಭರವಸೆಯನ್ನು ನೀಡುವುದು “ನಮ್ಮ ಮನೆ”ಯ ಆಶಯ. ಇದು ಕೇವಲ ಆಶ್ರಯ ತಾಣವಲ್ಲ; ಪ್ರೀತಿ, ಗೌರವ ಮತ್ತು ನೆಮ್ಮದಿಯೊಂದಿಗೆ ಬದುಕುವ ಒಂದು ದೊಡ್ಡ ಕುಟುಂಬ.",
      ],
    },
  },
  {
    id: "tavaru",
    icon: HeartHandshake,
    name: "Tavaru",
    knName: "ತವರು",
    tagline: "A Place to Begin Again",
    desc: "A safe shelter for vulnerable women who have faced abuse, trafficking or displacement — offering counselling, vocational training, education and rehabilitation support.",
    image: "ceremony",
    emblem: "/assets/programs/tavaru.png",
    emblemBg: "#F3EBDD",
    scene: "/assets/programs/scene-tavaru.png",
    facts: [
      ["Campus", "~10 acres"],
      ["Phase 1", "₹20 Crores — facilities for 500 women"],
      ["Full Vision", "₹70 Crores rehabilitation centre"],
    ],
    facilities: [
      "Safe shelter & counselling",
      "Handloom & textile production unit",
      "Adult education & vocational training",
      "Organic & livelihood farming",
      "Ayurvedic medicine preparation & training",
      "Financial literacy",
      "Health & wellness",
      "Community support",
    ],
    long: [
      "“Tavaru” is a rehabilitation and skill-development centre dedicated to providing people who have faced bitter experiences in life, those who have been socially exploited, and those trapped in the sex trade with an opportunity to rebuild their lives with dignity, self-reliance, and a new future.",
      "Spread across approximately 10 acres, the “Tavaru” project is estimated to have a total cost of ₹70 crore. In the first phase, IBMCT has resolved to invest approximately ₹20 crore to provide 500 people with shelter, education, skill development, and opportunities for dignified employment.",
      "The rehabilitation envisioned under “Tavaru” is not merely about providing shelter. Its primary objective is to equip people with skills in their hands, employment for their livelihood, dignity for their hard work, and hope for their future.",
      "A handloom and textile production unit will be established within the campus, providing employment and vocational training to approximately 500 people. Suitable markets will be created for the products they make, and IBMCT will ensure that every individual is provided with respectful remuneration/wages for their work. This will enable them to earn an income through their skills, become financially self-reliant, and live with dignity in society.",
      "Along with this, centres for education, vocational skill development, organic farming, Ayurvedic medicine preparation, and healthcare training will be established. Employment and income-generating opportunities will also be created through agriculture and other livelihood activities.",
      "“Tavaru” is not a place that defines anyone by their past; it is a place that helps build their future.",
    ],
    pledgeIntro: "Here, everyone will be given an opportunity:",
    pledge: [
      "To regain the dignity they have lost,",
      "To build their own lives with their own hands,",
      "To receive fair recognition and rewards for their hard work, and",
      "To develop the confidence to say, “I too can live with dignity.”",
    ],
    pledgeClosing:
      "“Tavaru” is not merely a rehabilitation centre; It is a new home for a new beginning — leading from pain to peace, from dependence to self-reliance, and from helplessness to self-respect.",
    kn: {
      tagline: "ಹೊಸ ಬದುಕಿನ ತಾಣ",
      desc: "ಸಾಮಾಜಿಕವಾಗಿ ಶೋಷಣೆಗೊಳಗಾದ ಮಹಿಳೆಯರಿಗೆ ಸುರಕ್ಷಿತ ಆಶ್ರಯ, ಸಮಾಲೋಚನೆ, ಕೌಶಲ್ಯ ತರಬೇತಿ ಮತ್ತು ಸ್ವಾವಲಂಬನೆಯತ್ತ ಕರೆದೊಯ್ಯುವ ಹೊಸ ಬದುಕು.",
      facts: [
        ["ವಿಸ್ತೀರ್ಣ", "ಸುಮಾರು 10 ಎಕರೆ"],
        ["ಮೊದಲ ಹಂತ", "₹20 ಕೋಟಿ — 500 ಮಹಿಳೆಯರಿಗೆ ಸೌಲಭ್ಯ"],
        ["ಪೂರ್ಣ ಸಂಕಲ್ಪ", "₹70 ಕೋಟಿ ಪುನರ್ವಸತಿ ಕೇಂದ್ರ"],
      ],
      facilities: [
        "ಸುರಕ್ಷಿತ ಆಶ್ರಯ ಮತ್ತು ಸಮಾಲೋಚನೆ",
        "ಹ್ಯಾಂಡ್‌ಲೂಮ್ ಮತ್ತು ಜವಳಿ ಉತ್ಪಾದನಾ ಘಟಕ",
        "ವಯಸ್ಕರ ಶಿಕ್ಷಣ ಮತ್ತು ವೃತ್ತಿಪರ ತರಬೇತಿ",
        "ಸಾವಯವ ಮತ್ತು ಜೀವನೋಪಾಯ ಕೃಷಿ",
        "ಆಯುರ್ವೇದ ಔಷಧ ತಯಾರಿಕೆ ಮತ್ತು ತರಬೇತಿ",
        "ಹಣಕಾಸು ಸಾಕ್ಷರತೆ",
        "ಆರೋಗ್ಯ ಮತ್ತು ಕ್ಷೇಮ",
        "ಸಮುದಾಯ ಬೆಂಬಲ",
      ],
      long: [
        "ತವರು — ಸಾಮಾಜಿಕವಾಗಿ ಶೋಷಣೆಗೊಳಗಾದವರು ಹಾಗೂ ಲೈಂಗಿಕ ವೃತ್ತಿಯಲ್ಲಿ ಸಿಲುಕಿರುವ ಮಹಿಳೆಯರಿಗೆ ಸುರಕ್ಷಿತ ಆಶ್ರಯ, ಶಿಕ್ಷಣ, ಕೌಶಲ್ಯಾಭಿವೃದ್ಧಿ ಮತ್ತು ಸ್ವಾವಲಂಬನೆಯ ಮೂಲಕ ಗೌರವಯುತ ಬದುಕಿಗೆ ಹೊಸ ಅವಕಾಶ ಕಲ್ಪಿಸುವ ಸಮಗ್ರ ಪುನರ್ವಸತಿ ಕೇಂದ್ರ.",
        "ಸುಮಾರು 10 ಎಕರೆ ಪ್ರದೇಶದಲ್ಲಿ ರೂಪುಗೊಳ್ಳಲಿರುವ ಈ ಯೋಜನೆಯ ಒಟ್ಟು ಅಂದಾಜು ವೆಚ್ಚ ₹70 ಕೋಟಿ. ಮೊದಲ ಹಂತದಲ್ಲಿ ₹20 ಕೋಟಿ ವೆಚ್ಚದಲ್ಲಿ 500 ಜನರಿಗೆ ಅಗತ್ಯ ಸೌಲಭ್ಯಗಳನ್ನು ಒದಗಿಸಲಾಗುವುದು. ಹ್ಯಾಂಡ್‌ಲೂಮ್ ಮತ್ತು ಜವಳಿ ಉತ್ಪಾದನಾ ಘಟಕದ ಮೂಲಕ ಸುಮಾರು 500 ಜನರಿಗೆ ಉದ್ಯೋಗ ಹಾಗೂ ವೃತ್ತಿಪರ ತರಬೇತಿ ನೀಡಲಾಗುವುದು.",
        "“ತವರು” ಅವರ ಹಿಂದಿನ ಬದುಕನ್ನು ಪ್ರಶ್ನಿಸುವ ಸ್ಥಳವಲ್ಲ; ಹೊಸ ಬದುಕಿಗೆ ದಾರಿ ತೋರಿಸುವ ಸ್ಥಳ. ನೋವಿನಿಂದ ನೆಮ್ಮದಿಯತ್ತ ಮತ್ತು ಅವಲಂಬನೆಯಿಂದ ಸ್ವಾವಲಂಬನೆಯತ್ತ ಕರೆದೊಯ್ಯುವ ಹೊಸ ಬದುಕಿನ ತಾಣ.",
      ],
    },
  },
  {
    id: "thottilu",
    icon: Sprout,
    name: "Thottilu",
    knName: "ತೊಟ್ಟಿಲು",
    tagline: "Every Child Deserves a Beginning",
    desc: "Supporting underprivileged children from marginalized communities with nutrition, healthcare, early education and emotional support.",
    image: "outdoor",
    emblem: "/assets/programs/thottilu.png",
    emblemBg: "#F3EBDD",
    scene: "/assets/programs/scene-thottilu.png",
    facts: [
      ["Campus", "~5 acres"],
      ["Phase 1", "₹10 Crores — 500 children"],
      ["Full Vision", "₹50 Crores child development centre"],
    ],
    facilities: [
      "Well-equipped residential facilities",
      "Nutritious food",
      "Quality education",
      "Healthcare services",
      "Study rooms",
      "Library",
      "Playground",
      "Indoor sports facilities",
      "Recreation room",
      "Art, music & cultural activities",
    ],
    long: [
      "“Thottilu” is not merely a shelter for children; it is a new beginning that restores hope and trust in life for young lives who have been deprived of love, affection, and a sense of belonging.",
      "Every child deserves to grow up surrounded by a mother’s affection, a father’s protection, and the love of a family. However, due to circumstances beyond their control, some young lives are deprived of this love and support. “Thottilu” is a dream to wipe away the tears of such children, bring smiles back to their faces, and give them the heartfelt assurance: “You are not alone. We are with you.”",
      "Spread across approximately 5 acres of spacious land, this ambitious project is planned to be developed in phases. The total estimated cost of the project is ₹50 crore, and in the first phase, IBMCT has resolved to establish a well-equipped centre at an estimated cost of ₹10 crore, providing shelter, care, and essential facilities for 500 children.",
      "Along with providing a safe and secure home for homeless and orphaned children, the primary objective of this project is to provide them with love, care, education, and a better future. Here, a child will receive not merely a place to live, but a home they can call their own, people who embrace them with love, and a family that inspires them to dream.",
      "The centre will provide well-equipped residential facilities, nutritious food, quality education, healthcare services, study rooms, a library, a playground, indoor sports facilities, a recreation room, and facilities for art, music, and cultural activities.",
      "Beyond the physical development of children, “Thottilu” places special emphasis on understanding their emotional pain and nurturing their self-confidence and self-respect. The hidden talents of every child will be identified, their dreams will be given wings, and special guidance will be provided to help them grow into self-reliant and responsible citizens.",
      "Holding the hand of a child is not merely about helping one life; it is about shaping an entire future.",
      "“Thottilu” — a home where every little dream begins with love and grows with hope.",
    ],
    kn: {
      tagline: "ಪ್ರತಿ ಮಗುವಿಗೂ ಒಂದು ಆರಂಭ",
      desc: "ಅನಾಥ ಹಾಗೂ ಆಸರೆಯಿಲ್ಲದ ಮಕ್ಕಳಿಗೆ ಪ್ರೀತಿ ಮತ್ತು ಆರೈಕೆಯ ಜೊತೆಗೆ ಗುಣಮಟ್ಟದ ಶಿಕ್ಷಣ, ಆಧುನಿಕ ಕೌಶಲ್ಯಗಳು ಮತ್ತು ಸುರಕ್ಷಿತ ಬಾಲ್ಯದ ನೆಲೆ.",
      facts: [
        ["ವಿಸ್ತೀರ್ಣ", "ಸುಮಾರು 5 ಎಕರೆ"],
        ["ಮೊದಲ ಹಂತ", "₹10 ಕೋಟಿ — 500 ಮಕ್ಕಳಿಗೆ"],
        ["ಪೂರ್ಣ ಸಂಕಲ್ಪ", "₹50 ಕೋಟಿ ಮಕ್ಕಳ ಅಭಿವೃದ್ಧಿ ಕೇಂದ್ರ"],
      ],
      facilities: [
        "ಡೇಕೇರ್ (6 ತಿಂಗಳು–5 ವರ್ಷ)",
        "ಶಿಶು ವಿಹಾರ (LKG–UKG)",
        "ಪ್ರಾಥಮಿಕ (1–7 ನೇ ತರಗತಿ)",
        "ಪ್ರೌಢಶಾಲೆ (8–10) ಮತ್ತು ಪಿಯುಸಿ",
        "ಕಂಪ್ಯೂಟರ್ ಮತ್ತು AI ಪ್ರಯೋಗಾಲಯ",
        "ಗ್ರಂಥಾಲಯ ಮತ್ತು ಅಧ್ಯಯನ ಕೊಠಡಿಗಳು",
        "ಆಟದ ಮೈದಾನ ಮತ್ತು ಒಳಾಂಗಣ ಕ್ರೀಡೆ",
        "ಕಲೆ, ಸಂಗೀತ ಮತ್ತು ಸಾಂಸ್ಕೃತಿಕ ಚಟುವಟಿಕೆಗಳು",
        "ಶಾಲಾ ಊಟಾಲಯ",
      ],
      long: [
        "ತೊಟ್ಟಿಲು — ಪ್ರೀತಿ, ಮಮತೆ ಮತ್ತು ಕುಟುಂಬದ ಆಸರೆಯಿಂದ ವಂಚಿತರಾದ ಅನಾಥ ಹಾಗೂ ನಿರಾಶ್ರಿತ ಮಕ್ಕಳಿಗೆ ಸುರಕ್ಷಿತ ಆಶ್ರಯ, ಆರೈಕೆ, ಶಿಕ್ಷಣ ಮತ್ತು ಭರವಸೆಯ ಭವಿಷ್ಯವನ್ನು ಒದಗಿಸುವ ಸಮಗ್ರ ಮಕ್ಕಳ ಅಭಿವೃದ್ಧಿ ಕೇಂದ್ರ.",
        "ಸುಮಾರು 5 ಎಕರೆ ಪ್ರದೇಶದಲ್ಲಿ ರೂಪುಗೊಳ್ಳಲಿರುವ ಈ ಯೋಜನೆಯ ಒಟ್ಟು ಅಂದಾಜು ವೆಚ್ಚ ₹50 ಕೋಟಿ. ಮೊದಲ ಹಂತದಲ್ಲಿ ₹10 ಕೋಟಿ ವೆಚ್ಚದಲ್ಲಿ 500 ಮಕ್ಕಳಿಗೆ ಅಗತ್ಯವಾದ ವಸತಿ, ಆರೈಕೆ ಮತ್ತು ಅಭಿವೃದ್ಧಿ ಸೌಲಭ್ಯಗಳನ್ನು ಕಲ್ಪಿಸಲಾಗುವುದು. ಡೇಕೇರ್‌ನಿಂದ ಹಿಡಿದು ಪ್ರೌಢಶಾಲೆ, ಪಿಯುಸಿ ಮತ್ತು ಮತ್ತಷ್ಟು — ಪ್ರತಿ ಮಗುವಿನ ಪ್ರಯಾಣಕ್ಕೆ ಜೊತೆಯಾಗಿ.",
        "ಆಧುನಿಕ ಶಿಕ್ಷಣದ ಜೊತೆಗೆ ಕಂಪ್ಯೂಟರ್, ಕೃತಕ ಬುದ್ಧಿಮತ್ತೆ (AI), ಕೌಶಲ್ಯಾಭಿವೃದ್ಧಿ ಮತ್ತು ಜೀವನ ಕೌಶಲ್ಯ ತರಬೇತಿಗೆ ಆದ್ಯತೆ. ತೊಟ್ಟಿಲು ಪ್ರೀತಿಯಿಂದ ಬೆಳೆಸಿ, ಶಿಕ್ಷಣದಿಂದ ಸಬಲಗೊಳಿಸಿ, ಭರವಸೆಯಿಂದ ಭವಿಷ್ಯ ಕಟ್ಟುವ ಮನೆ.",
      ],
    },
  },
];

export const beyondPrograms = [
  { id: "dhamma-meditation", icon: Flower2, label: "Dhamma & Meditation", knLabel: "ಧಮ್ಮ ಮತ್ತು ಧ್ಯಾನ" },
  { id: "nalanda-study-centre", icon: BookOpen, label: "Nalanda Study Centre", knLabel: "ನಾಳಂದ ಅಧ್ಯಯನ ಕೇಂದ್ರ" },
  { id: "anna-dana", icon: Soup, label: "Anna Dana", knLabel: "ಅನ್ನ ದಾನ" },
  { id: "education-scholarships", icon: GraduationCap, label: "Education & Scholarships", knLabel: "ಶಿಕ್ಷಣ ಮತ್ತು ವಿದ್ಯಾರ್ಥಿವೇತನ" },
  { id: "medical-camps", icon: HeartPulse, label: "Medical Camps", knLabel: "ವೈದ್ಯಕೀಯ ಶಿಬಿರ" },
  { id: "environment", icon: Leaf, label: "Environment", knLabel: "ಪರಿಸರ" },
  { id: "buddhist-heritage", icon: Landmark, label: "Buddhist Heritage", knLabel: "ಬೌದ್ಧ ಪರಂಪರೆ" },
  { id: "karuna-seva", icon: HandHeart, label: "Karuna Seva", knLabel: "ಕರುಣಾ ಸೇವೆ" },
];

export const servicePages = {
  "dhamma-meditation": {
    icon: Flower2,
    title: "Dhamma & Meditation",
    tagline: "Calm, Clarity, Compassion",
    image: "svcDhammaMeditation",
    body: [
      "Regular meditation sittings, retreats and Dhamma teachings open to all — cultivating calm, clarity and compassion in daily life. Sessions are led by monastics and welcome beginners and experienced practitioners alike.",
      "From weekly group sittings to longer retreats at the Nalanda Study Centre, IBMCT keeps the Buddha’s path of practice accessible to everyone, regardless of background or belief.",
    ],
  },
  "nalanda-study-centre": {
    icon: BookOpen,
    title: "Nalanda Study Centre",
    tagline: "Reviving the Spirit of Nalanda",
    image: "svcNalanda",
    body: [
      "A living centre for Dhamma, meditation, scholarship and cultural preservation — completed in 2024 in Karnataka. The centre will host courses, seminars, a public library and monastic training.",
      "Inspired by the ancient Nalanda university, the centre carries a modern mission: a place where the Dhamma is studied, practised and shared freely with all.",
    ],
    link: { to: "/nalanda", label: "Explore the Full Nalanda Project" },
  },
  "anna-dana": {
    icon: Soup,
    title: "Anna Dana",
    tagline: "The Gift of Food",
    image: "svcAnnaDana",
    body: [
      "Anna Dana — the offering of food — is among the oldest and most honoured acts of giving in the Buddhist tradition. IBMCT serves nutritious meals to elders, children, patients and monastics across its programs.",
      "Every meal is prepared and served with care, because nourishment is not only for the body — it is a message that someone cares.",
    ],
  },
  "education-scholarships": {
    icon: GraduationCap,
    title: "Education & Scholarships",
    tagline: "Opening Doors Through Learning",
    image: "svcEducation",
    body: [
      "IBMCT supports students from underprivileged families with scholarships, school supplies, uniforms and mentoring — so that financial hardship never decides a child’s future.",
      "From primary school to higher education, the Trust walks alongside students for the long term, celebrating every examination passed and every door opened.",
    ],
  },
  "medical-camps": {
    icon: HeartPulse,
    title: "Medical Camps",
    tagline: "Healthcare That Reaches the Last Mile",
    image: "svcMedicalCamps",
    body: [
      "IBMCT organises regular free medical camps that carry doctors, nurses, medicines and preventive care to remote and underserved villages around Chamarajanagar and beyond.",
      "Each camp offers consultations, basic diagnostics, medicines and referrals — delivered with the same dignity a patient would receive in any city hospital.",
    ],
  },
  environment: {
    icon: Leaf,
    title: "Environment",
    tagline: "Caring for Our Shared Home",
    image: "outdoor",
    body: [
      "The Buddha taught in forests, under trees, in harmony with the natural world. IBMCT honours that legacy through tree-planting drives, care for green spaces and environmental awareness in the communities it serves.",
      "Every sapling planted is a small act of faith in the future — shade, fruit and clean air for generations we will never meet.",
    ],
  },
  "buddhist-heritage": {
    icon: Landmark,
    title: "Buddhist Heritage",
    tagline: "Preserving a Priceless Legacy",
    image: "svcBuddhistHeritage",
    body: [
      "From the Sacred Buddha Relic Initiative to advocacy for the protection of the Ashokan Rock Edicts, IBMCT works to preserve, honour and share India’s profound Buddhist heritage.",
      "Through exhibitions, talks and partnerships with scholars and communities, the Trust keeps this inheritance alive for future generations — in Karnataka and across the world.",
    ],
  },
  "karuna-seva": {
    icon: HandHeart,
    title: "Karuna Seva",
    tagline: "Compassion in Action",
    image: "svcKarunaSeva",
    body: [
      "Karuna Seva is IBMCT’s open door for service — hands-on volunteer work wherever the need is greatest: flood relief, village outreach, care visits, event support and more.",
      "It is the living expression of karuna — compassion that does not stop at feeling, but moves the hands and feet to help.",
    ],
    link: { to: "/volunteer", label: "Join Karuna Seva as a Volunteer" },
  },
};

export const journeyMilestones = [
  { year: "2007", en: "Ordination at Bodh Gaya", kn: "ಬೋಧ ಗಯದಲ್ಲಿ ದೀಕ್ಷೆ" },
  { year: "2012", en: "First 90 KM Padayatra", kn: "ಮೊದಲ 90 ಕಿ.ಮೀ. ಪಾದಯಾತ್ರೆ" },
  { year: "2015", en: "Sacred Buddha Relic Initiative", kn: "ಪವಿತ್ರ ಬುದ್ಧ ಧಾತು ಯೋಜನೆ" },
  { year: "2019", en: "30,000+ KM Padayatra Completed", kn: "30,000+ ಕಿ.ಮೀ. ಪಾದಯಾತ್ರೆ ಪೂರ್ಣಗೊಂಡಿತು" },
  { year: "2025", en: "Ashokan Rock Edicts Advocacy", kn: "ಅಶೋಕನ ಶಿಲಾಶಾಸನಗಳ ಪ್ರಚಾರ" },
  { year: "Today", en: "International Dhamma & Service", kn: "ಅಂತರರಾಷ್ಟ್ರೀಯ ಧಮ್ಮ ಮತ್ತು ಸೇವೆ" },
];

export const ADDRESS =
  "Uttavalli, Behind Government Medical College, Chamarajanagar, Karnataka – 571313, India";
export const EMAIL = "info@ibmct.org";
